import React from 'react';
import uniqBy from 'lodash.uniqby';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import { DateTime } from 'luxon';

import { WEDDING_EVENT_STEPS } from './constants';
import uuidv4 from '../../../../../util/uuid';
import { isValidEmail } from '../../../../../util/email';
import WeddingEventCreateDetailsStep from './components/WeddingEventCreateDetailsStep';
import WeddingEventCreateEventsStep from './components/WeddingEventCreateEventsStep';
import WeddingEventCreateJoinCodeStep from './components/WeddingEventCreateJoinCodeStep';
import WeddingEventCreateSocialStep from './components/WeddingEventCreateSocialStep';
import WeddingEventCreateReviewStep from './components/WeddingEventCreateReviewStep/WeddingEventCreateReviewStep';
import { BASENAME } from '../../../../../constants/basenames';
import useBasename from '../../../../../components/routing/useBasename';
import EventCreateShutterbugPermissionsStep from '../FreemiumEventCreateWizard/components/EventCreateShutterbugPermissionsStep';
import BoxedCustomControl from '../../../../../components/global/BoxedCustomControl';
import Button from '../../../../../components/global/Button';
import Alert from '../../../../../components/global/Alert';
import { getAsset } from '../../../../../util/assets';
import { joinCodeSearch } from '../../../../web-gallery/views/AlbumSearch/graphql';
import { getErrorString } from '../../../../../util/normalize-errors';
import {
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
  ALBUM_UPLOAD_PERMISSIONS,
} from '../../../../../constants/albums';
import {
  CONTRIBUTOR_UPLOAD_ACCESS,
  CONTRIBUTOR_VIEW_ACCESS,
  PERMISSION_NONE,
} from '../../../../../constants/contributors';
import { getWatermarkConfigInput } from '../../Album/AlbumSettings/AlbumBrandFilterSettings/helpers';
import { ALBUM_FOLDER_JOIN_TYPE } from '../../../../../constants/album-folder';
import {
  generateMachineJoinCode,
  isValidJoinCode,
} from '../../../../../util/join-code';

export const defaultWeddingAlbums = [
  'Engagement Party',
  'Bridal Shower',
  'Rehearsal Dinner',
  'Family Brunch',
].map(eventName => ({
  name: eventName,
  id: uuidv4(),
  selected: false,
  removable: false,
  available: true,
  photo_album: true,
}));

export const getEventItemByPlan = (eventItems, plan) => {
  return eventItems.find(eventItem => eventItem.plan === plan);
};

export const getEventCrateSteps = ({ stepProps = {}, setStepProps }) => {
  const basename = useBasename();
  const showProfileEmail = basename === BASENAME.SHUTTERBUG;
  const { purchasedWeddingEvent, checkoutSuccess } = stepProps;

  return {
    [WEDDING_EVENT_STEPS.DETAILS]: {
      label: 'Configuration',
      key: WEDDING_EVENT_STEPS.DETAILS,
      title: <>Let’s start setting up your wedding event!</>,
      layoutSize: 'sb',
      component: props => (
        <>
          <WeddingEventCreateDetailsStep
            stepProps={stepProps}
            setStepProps={setStepProps}
            {...props}
          />
        </>
      ),
      validate: values => {
        const errors = {};

        if (!values.name) {
          errors.name = 'Please provide a name for your gallery';
        }

        if (values.name && values.name.length > 120) {
          errors.name = 'Gallery name cannot be longer than 120 characters.';
        }

        if (showProfileEmail && !isValidEmail(values.email_address)) {
          errors.email_address =
            'Please provide a valid email address for your account';
        }

        if (!showProfileEmail && !isValidEmail(values.couple_email_address)) {
          errors.couple_email_address = 'Please provide a valid email address';
        }

        if (!values.groom_name) {
          errors.groom_name = 'Please provide Bride / Groom’s name';
        }

        if (!values.bride_name) {
          errors.bride_name = 'Please provide Bride / Groom’s name';
        }

        if (!values.date) {
          errors.date = 'Please add your wedding date.';
        }

        return errors;
      },
    },
    [WEDDING_EVENT_STEPS.EVENTS]: {
      label: 'Albums',
      key: WEDDING_EVENT_STEPS.EVENTS,
      title: "Let's create albums for the events you'd like to Waldo-fy!",
      layoutSize: 'sb',
      component: props => (
        <WeddingEventCreateEventsStep
          stepProps={stepProps}
          setStepProps={setStepProps}
          {...props}
        />
      ),
      validate: values => {
        const errors = {};

        if (!values.albums.find(album => album.selected)) {
          errors.albums = 'Please select at least 1 album to create';
        }

        return errors;
      },
    },
    [WEDDING_EVENT_STEPS.PERMISSIONS]: {
      label: 'Permissions',
      key: WEDDING_EVENT_STEPS.PERMISSIONS,
      title: 'Let’s get your gallery set-up!',
      layoutSize: 'sm',
      component: props => {
        return (
          <>
            <EventCreateShutterbugPermissionsStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
    },
    [WEDDING_EVENT_STEPS.JOIN_CODE]: {
      label: 'Join Code',
      key: WEDDING_EVENT_STEPS.JOIN_CODE,
      title: stepProps?.purchasedWeddingEventFeatures?.join_code_enabled
        ?.enabled ? (
        "Let's create a personalized SMS code for guests to join your gallery!"
      ) : (
        <>
          Upgrade to <span className="text-extra-bold text-primary">Gold</span>{' '}
          or <span className="text-extra-bold text-primary">Platinum</span> to
          create a personalized SMS code for guests to join your event or{' '}
          <span className="text-extra-bold text-primary">skip this step</span>.
        </>
      ),
      layoutSize: 'lg',
      component: props => {
        return (
          <>
            <WeddingEventCreateJoinCodeStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
      validate: async (values, apolloClient) => {
        const errors = {};

        if (values.join_code && values.join_code !== values.join_code_confirm) {
          errors.join_code = 'Provided Join Codes do not match.';
        }

        if (
          values.join_code &&
          values.join_code === values.join_code_confirm &&
          !isValidJoinCode(values.join_code)
        ) {
          errors.join_code = 'The join code provided is not valid.';
        }

        if (values.public_join_code_verification_enabled && !values.join_code) {
          errors.public_join_code_verification_enabled =
            'Please add a Join Code or disable "Require Join Code to view".';
        }

        if (errors.join_code) {
          // Returns existing errors without validation.
          return errors;
        }

        if (values.join_code) {
          try {
            const {
              data: { joinCodeByCode },
            } = await apolloClient.query({
              query: joinCodeSearch,
              variables: { code: values.join_code },
            });

            if (joinCodeByCode?.album?.id || joinCodeByCode?.album_folder?.id) {
              errors.join_code =
                'Provided Join Code already exist. Please select a different one.';
            }
          } catch (err) {
            errors.join_code = getErrorString(err);
            return errors;
          }
        }

        return errors;
      },
      skip:
        purchasedWeddingEvent &&
        // We can determine if the user went through stripe flow based off of this value.
        !checkoutSuccess &&
        !purchasedWeddingEvent?.features?.join_code_enabled,
      skippable: () => {
        return true;
      },
      onSkip: ({ handleChange }) => {
        handleChange({
          target: {
            name: 'join_code',
            value: '',
          },
        });
        handleChange({
          target: {
            name: 'join_code_confirm',
            value: '',
          },
        });
        handleChange({
          target: {
            name: 'public_join_code_verification_enabled',
            value: false,
          },
        });
      },
    },
    [WEDDING_EVENT_STEPS.SOCIAL]: {
      label: 'Social',
      key: WEDDING_EVENT_STEPS.SOCIAL,
      title:
        stepProps?.purchasedWeddingEventFeatures?.photo_watermark_enabled
          ?.enabled &&
        stepProps?.purchasedWeddingEventFeatures?.custom_hashtag_enabled
          ?.enabled ? (
          'Want to add your own filter to your photos and a hashtag?'
        ) : (
          <>
            Upgrade to{' '}
            <span className="text-extra-bold text-primary">Gold</span> or{' '}
            <span className="text-extra-bold text-primary">Platinum</span> to
            add your own filter to your photos and a hashtag or{' '}
            <span className="text-extra-bold text-primary">skip this step</span>
            .
          </>
        ),
      layoutSize: 'md',
      component: props => {
        return (
          <>
            <WeddingEventCreateSocialStep
              stepProps={stepProps}
              setStepProps={setStepProps}
              {...props}
            />
          </>
        );
      },
      validate: values => {
        const errors = {};
        if (
          values.watermark_config?.asset_image?.id &&
          !values.watermark_config?.placement
        ) {
          errors.watermark_config =
            'Please select your photo overlay placement';
        }

        return errors;
      },
      skip:
        purchasedWeddingEvent &&
        // We can determine if the user went through stripe flow based off of this value.
        !checkoutSuccess &&
        !purchasedWeddingEvent?.features?.photo_watermark_enabled &&
        !purchasedWeddingEvent?.features?.custom_hashtag_enabled,
      skippable: () => {
        return true;
      },
      onSkip: ({ handleChange }) => {
        handleChange({
          target: {
            name: 'hash_code',
            value: '',
          },
        });
        handleChange({
          target: {
            name: 'watermark_config',
            value: {},
          },
        });
      },
    },
    [WEDDING_EVENT_STEPS.REVIEW]: {
      label: 'Review',
      key: WEDDING_EVENT_STEPS.REVIEW,
      title:
        'Please confirm your settings and click create below to Waldo-fy your wedding!',
      layoutSize: 'lg',
      component: props => {
        return (
          <WeddingEventCreateReviewStep
            stepProps={stepProps}
            setStepProps={setStepProps}
            {...props}
          />
        );
      },
      validate: () => {},
    },
  };
};

/**
 * Returns a list of albums available for the creation wizard. Relies on selected event & permissions to determine certain functionality.
 *
 * @param {Object} opts List of options.
 * @param {Object} opts.purchasedWeddingEvent The Wedding purchased event (if available, mainly used as fall back).
 * @param {Object} opts.purchasedWeddingEventFeatures Permission object.
 * @param {Array<Object>} opts.defaultAlbums List of albums that are either pre-created for the user, or created by the user.
 *
 * @returns {({is_video_album: boolean, removable: boolean, name: string, display_order: number, label: JSX.Element, id: string, selected: boolean}|{photo_album: boolean, removable: boolean, name: *, available: boolean, id: string, selected: boolean})[]}
 */
export const getCreateWizardAlbums = ({
  purchasedWeddingEvent,
  purchasedWeddingEventFeatures,
  defaultAlbums = defaultWeddingAlbums,
} = {}) => {
  const videoGuestBookEnabled =
    purchasedWeddingEventFeatures?.video_guestbook_enabled?.enabled ||
    purchasedWeddingEvent?.features.video_guestbook_enabled;

  const weddingDaySlideshowEnabled =
    purchasedWeddingEventFeatures?.slideshow_enabled?.enabled ||
    purchasedWeddingEvent?.features.slideshow_enabled;

  const memoryBookEnabled =
    purchasedWeddingEventFeatures?.memory_book_enabled?.enabled ||
    purchasedWeddingEvent?.features.memory_book_enabled;

  const weddingDayAlbumItem = {
    name: 'Wedding day',
    id: 'WEDDING_DAY_ALBUM',
    removable: false,
    is_wedding_day_album: true,
    display_order: 1,
    // Force selection all together.
    selected: true,
    permanent: true,
    createSlideshow: weddingDaySlideshowEnabled,
  };

  const memoryBookAlbumItem = {
    name: 'Memories Slideshow',
    id: 'MEMORY_BOOK_ALBUM',
    removable: false,
    is_memory_book: true,
    display_order: 2,
    // Makes sure every time we call this method this value is reset accordingly based on the available plans.
    selected: memoryBookEnabled,
  };

  const videoGuestBookAlbumItem = {
    name: 'Video Guestbook',
    id: 'VGB_ALBUM',
    removable: false,
    is_video_album: true,
    display_order: 3,
    // Makes sure every time we call this method this value is reset accordingly based on the available plans.
    selected: videoGuestBookEnabled,
  };

  return uniqBy(
    [
      {
        ...weddingDayAlbumItem,
        render: purchasedWeddingEvent
          ? undefined
          : ({ onUpgrade }) => (
              <BoxedCustomControl
                key={weddingDayAlbumItem.id}
                label={
                  <div className="d-flex align-items-center">
                    <span>Wedding Day</span>
                  </div>
                }
                type="checkbox"
                id={weddingDayAlbumItem.id}
                checked
                boxStyle="outlined"
                customControlProps={{
                  shape: 'squared',
                }}
                className="wedding-event-create-album-item"
              >
                <Alert type="blue-pale" className="text-primary" borderless>
                  <div className="d-flex align-items-center">
                    <div>
                      <p className="text-extra-bold text-uppercase text-sm mb-0">
                        Add on
                      </p>
                      <p>Live Smart Slideshow</p>
                    </div>
                    {weddingDaySlideshowEnabled ? (
                      <span className="text-primary text-extra-bold ml-auto">
                        Included
                      </span>
                    ) : (
                      <Button
                        btnStyle="reset"
                        onClick={onUpgrade}
                        btnSize="xs"
                        className="ml-auto btn-wedding-event-upgrade"
                      >
                        <img
                          src={getAsset('/diamond-orange.svg')}
                          alt="Unlock your potential"
                          width="18"
                          className="mr-1"
                        />{' '}
                        Upgrade
                      </Button>
                    )}
                  </div>
                </Alert>
              </BoxedCustomControl>
            ),
      },
      {
        ...memoryBookAlbumItem,
        render:
          purchasedWeddingEvent && !memoryBookEnabled
            ? undefined
            : ({ isSelected, onChange, onUpgrade }) => (
                <BoxedCustomControl
                  key={memoryBookAlbumItem.id}
                  label={
                    <div className="d-flex align-items-center">
                      <span>Memories Slideshow</span>
                    </div>
                  }
                  type="checkbox"
                  id={memoryBookAlbumItem.id}
                  checked={isSelected}
                  boxStyle="outlined"
                  customControlProps={{
                    shape: 'squared',
                  }}
                  className="wedding-event-create-album-item"
                  description="Easily gather memories of the bride & groom and automatically create a beautiful, chronological slideshow."
                  onChange={(...args) => {
                    if (memoryBookEnabled) {
                      onChange(...args);
                      return;
                    }

                    onUpgrade();
                  }}
                >
                  {!memoryBookEnabled && (
                    <Button
                      btnStyle="reset"
                      onClick={onUpgrade}
                      btnSize="xs"
                      className="ml-auto btn-wedding-event-upgrade"
                    >
                      <img
                        src={getAsset('/diamond-orange.svg')}
                        alt="Unlock your potential"
                        width="18"
                        className="ml-auto"
                      />{' '}
                      Upgrade
                    </Button>
                  )}
                </BoxedCustomControl>
              ),
      },
      {
        ...videoGuestBookAlbumItem,
        render:
          purchasedWeddingEvent && !videoGuestBookEnabled
            ? () => null // Render null component.
            : ({ isSelected, onChange, onUpgrade }) => (
                <BoxedCustomControl
                  key={videoGuestBookAlbumItem.id}
                  label={
                    <div
                      className="d-flex align-items-center"
                      style={{ marginTop: '-8px' }}
                    >
                      <span>Video Guestbook</span>
                    </div>
                  }
                  description="Video montage of 30-second messages and well wishes for you!"
                  type="checkbox"
                  id={videoGuestBookAlbumItem.id}
                  checked={videoGuestBookEnabled && isSelected}
                  boxStyle="outlined"
                  onChange={(...args) => {
                    if (videoGuestBookEnabled) {
                      onChange(...args);
                      return;
                    }

                    onUpgrade();
                  }}
                  customControlProps={{
                    shape: 'squared',
                  }}
                  className="wedding-event-create-album-item"
                >
                  {!videoGuestBookEnabled && (
                    <Button
                      btnStyle="reset"
                      onClick={onUpgrade}
                      btnSize="xs"
                      className="ml-auto btn-wedding-event-upgrade"
                    >
                      <img
                        src={getAsset('/diamond-orange.svg')}
                        alt="Unlock your potential"
                        width="18"
                        className="mr-1"
                      />{' '}
                      Upgrade
                    </Button>
                  )}
                </BoxedCustomControl>
              ),
      },
      ...defaultAlbums,
    ].filter(Boolean),
    'id',
  );
};

export const onCreateWeddingsAlbumFolder = async ({
  values,
  albumFolderCreate,
  albumCreate,
  purchasedEventId,
  user,
}) => {
  // Assign album to folder:
  const {
    data: {
      albumFolderCreate: { album_folder: albumFolder },
    },
  } = await albumFolderCreate({
    account_id: user?.id,
    album_type: ALBUM_TYPE.EVENT,
    album_subtype: ALBUM_SUBTYPE.WEDDINGS,
    purchased_event_id: purchasedEventId,
    name: values.name,
    privacy: values.privacy,
    start_date: startOfDay(values.date),
    end_date: endOfDay(values.date),
    join_code: values.join_code,
    // is_demo: true --- Set from platform based on type/subtype.
    auto_contributor_settings:
      values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE
        ? {
            upload_access: values.require_review
              ? CONTRIBUTOR_UPLOAD_ACCESS.REVIEW
              : CONTRIBUTOR_UPLOAD_ACCESS.PUBLISH,
            view_access: CONTRIBUTOR_VIEW_ACCESS.ALL,
          }
        : {
            view_access: CONTRIBUTOR_VIEW_ACCESS.ALL,
            upload_access: PERMISSION_NONE,
          },
    external_uploads_require_review: values.require_review,
    member_uploads_require_review: values.require_review,
    password: values.password,
    invite_only_access: values.invite_only_access,
    is_web_gallery: true,
    is_private_web_gallery: false,
    web_gallery_print_purchases_enabled: true,
    watermark: getWatermarkConfigInput(values.watermark_config),
    hash_code: values.hash_code,
    join_type: ALBUM_FOLDER_JOIN_TYPE.ALL,
    display_photos_attribution: true,
    display_public_photo_attribution: true,
    consumer_display_empty_albums: true,
    event_settings: {
      bride_name: values.bride_name,
      groom_name: values.groom_name,
      couple_email_address: values.couple_email_address,
      event_date: values.date
        ? DateTime.fromJSDate(values.date).toISODate()
        : null,
    },
    // TODO: Remove comments once supported:
    // status: values.status
  });

  const newAlbums = values.albums.filter(album => album.selected);

  if (newAlbums.length > 0) {
    await Promise.all(
      newAlbums.map(async album => {
        const {
          data: {
            albumCreate: { album: createdAlbum },
          },
        } = await albumCreate({
          name: album.name,
          parent_album_folder_id: albumFolder.id,
          creator_id: user?.id,
          password: values.password,
          invite_only_access: values.invite_only_access,
          privacy: values.privacy,
          album_type: ALBUM_TYPE.EVENT,
          album_subtype: ALBUM_SUBTYPE.WEDDINGS,
          join_code: generateMachineJoinCode(),
          is_video_album: album.is_video_album,
          display_order: album.display_order,
          enable_external_upload:
            values.permission === ALBUM_UPLOAD_PERMISSIONS.ANYONE,
          external_uploads_require_review: values.require_review,
          is_wedding_day_album: album.is_wedding_day_album,
          is_memory_book: album.is_memory_book,
          slideshow: album?.createSlideshow
            ? { mode: ALBUM_SLIDESHOW_MODE.ALL_PHOTOS }
            : undefined,
        });

        return createdAlbum;
      }),
    );
  }

  return albumFolder;
};
